import React, { useState } from 'react';
import { setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { reauthenticate } from '../auth';
import QRCode from 'qrcode';

/** sessionStorage guard so a stale-token recovery re-auth happens at most once
 *  per tab session (prevents a redirect loop if the scope is still absent). */
const REAUTH_FLAG = 'mfaScopeReauthTried';

/**
 * Cognito rejects the self-service MFA APIs (AssociateSoftwareToken etc.) with
 * "Access Token does not have required scopes" when the access token lacks
 * aws.cognito.signin.user.admin. Detect that specific failure so we can recover
 * by forcing a fresh authorization (DAP-18).
 */
export function isMissingScopeError(e: unknown): boolean {
  const msg = (e instanceof Error ? e.message : String(e ?? '')).toLowerCase();
  return msg.includes('required scopes');
}

export function friendlyAuthError(e: unknown, fallback: string): string {
  const name = e instanceof Error ? e.name : '';
  if (name === 'CodeMismatchException') return 'That code did not match — check your authenticator and try again.';
  if (name === 'ExpiredCodeException' || name === 'EnableSoftwareTokenMFAException') return 'That code expired — enter the current 6-digit code.';
  if (isMissingScopeError(e)) return 'Your sign-in session needs refreshing before MFA setup. Please sign out and sign in again, then set up two-factor authentication.';
  return e instanceof Error && e.message ? e.message : fallback;
}

interface Props {
  email: string;
  onEnrolled: () => void; // caller refetches /api/me (or reloads)
}

type Step = 'start' | 'scan' | 'done';

const MfaEnroll: React.FC<Props> = ({ email, onEnrolled }) => {
  const [step, setStep] = useState<Step>('start');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const begin = async () => {
    setBusy(true); setError('');
    try {
      const out = await setUpTOTP();
      sessionStorage.removeItem(REAUTH_FLAG);
      const uri = out.getSetupUri('DI MCP Portal', email).toString();
      setSecret(out.sharedSecret);
      setQr(await QRCode.toDataURL(uri, { width: 256 }));
      setStep('scan');
    } catch (e) {
      // A token minted before the aws.cognito.signin.user.admin scope was granted
      // can't call the MFA APIs. Recover by re-authorizing ONCE (guarded against a
      // redirect loop); if it still fails afterwards, show actionable guidance.
      if (isMissingScopeError(e) && !sessionStorage.getItem(REAUTH_FLAG)) {
        sessionStorage.setItem(REAUTH_FLAG, '1');
        await reauthenticate(); // navigates away to the hosted UI, then returns
        return;
      }
      setError(friendlyAuthError(e, 'Could not start MFA setup.'));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      try {
        await verifyTOTPSetup({ code: code.trim() });
      } catch (e) {
        setError(friendlyAuthError(e, 'That code was not accepted — enter a fresh 6-digit code.'));
        return;
      }
      try {
        await updateMFAPreference({ totp: 'PREFERRED' });
      } catch {
        setError('Your authenticator was linked but could not be activated — enter a fresh code and try again.');
        return;
      }
      setStep('done');
      onEnrolled();
    } finally { setBusy(false); }
  };

  return (
    <div style={s.wrap}>
      <h2 style={s.h}>Set up two-factor authentication</h2>
      {error && <div style={s.err}>{error}</div>}
      {step === 'start' && (
        <>
          <p style={s.p}>Protect your account with an authenticator app (1Password, Google Authenticator, Authy).</p>
          <button type="button" style={s.btn} disabled={busy} onClick={begin}>{busy ? 'Starting…' : 'Begin setup'}</button>
        </>
      )}
      {step === 'scan' && (
        <>
          <p style={s.p}>1. Scan this QR code with your authenticator app:</p>
          {qr && <img src={qr} alt="TOTP QR code" style={s.qr} />}
          <p style={s.secret}>or enter the key manually: <code>{secret}</code></p>
          <p style={s.p}>2. Enter the 6-digit code it shows:</p>
          <input style={s.input} value={code} onChange={e => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} onKeyDown={e => { if (e.key === 'Enter' && !busy && code.trim().length === 6) confirm(); }} />
          <button type="button" style={s.btn} disabled={busy || code.trim().length !== 6} onClick={confirm}>{busy ? 'Verifying…' : 'Verify & enable'}</button>
        </>
      )}
      {step === 'done' && <p style={s.p}>✓ Two-factor authentication is enabled.</p>}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 420, margin: '2rem auto', background: '#fff', borderRadius: 8, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  h: { fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.75rem' },
  p: { fontSize: '0.9rem', color: '#444', margin: '0.5rem 0' },
  secret: { fontSize: '0.8rem', color: '#666', margin: '0.5rem 0', wordBreak: 'break-all' as const },
  qr: { display: 'block', margin: '0.5rem auto', borderRadius: 6 },
  input: { width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '1rem', letterSpacing: '0.2em', textAlign: 'center' as const, marginBottom: '0.75rem' },
  btn: { background: '#4a6cf7', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  err: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.85rem' },
};

export default MfaEnroll;
